/**
 * Analytics Hook
 *
 * TanStack Query hook for fetching analytics summary data.
 * Provides caching, loading states, and error handling.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchAnalyticsSummary } from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import type { AnalyticsTimeRange } from '@/types/analytics';
import type { AuditLogCategory } from '@/types/audit-log';

/**
 * Use Analytics Hook
 * Fetches analytics summary for the specified time range
 *
 * @param timeRange - Time range for analytics (1h, 24h, 7d, 30d, 90d)
 * @param categories - Optional category or array of categories to filter by
 * @returns TanStack Query result with analytics data
 */
export function useAnalytics(
  timeRange: AnalyticsTimeRange,
  categories?: AuditLogCategory | AuditLogCategory[]
) {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['analytics', timeRange, categories],
    queryFn: () => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchAnalyticsSummary(timeRange, token, categories);
    },
    staleTime: 300000, // 5 minutes — matches the server-side Redis TTL for analytics
    gcTime: 600000, // 10 minutes
    retry: 1, // One retry — repeated retries pile abandoned queries onto Postgres
    refetchOnWindowFocus: false,
  });
}
